import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Button, Card, ClickAwayListener, Grid, Grow, IconButton, Paper, Popper, Stack, Typography } from "@mui/material";
import CalculatorStepper from "../../calculatorStepper";
import { ButtonComponent, FormProviderComponent, SelectionComponent, TextFieldComponent } from "../../../../components/form";
import { useCallback, useEffect, useRef, useState } from 'react';
import SnackbarComponent from '../../../../components/snackBar/snackbarComponent';
import { VEHICLE } from '../../../../data/data';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import CustomMonthCalender from '../../../../components/calender/customMonthCalender';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Label = (label) => {
    return <Typography
        fontWeight={400}
        fontSize={{md: 16, lg: 22}}
        color="black"
    >
        {label}
    </Typography>
}

const CalculatorWebFilter = ({
    defaultData, 
    moveToFormView, 
    buttonLoading,
    allowDistanceEdit
}) => {
    const [allowBatta, setAllowBatta] = useState(true);

    const [openMonthPicker, setOpenMonthPicker] = useState(false);
    const anchorRef = useRef(null);

    const navigate = useNavigate();
    
    const formSchema = yup.object().shape({
        distance: yup.number().required('Distance is required').moreThan(0, 'Distance is required'),
        month: yup.string().required('Month is required'),
        vehicle: yup.string().required('Vehicle type is required'),
        days: yup.number().required('Number of days is required').typeError('Invalid number of days').moreThan(0, 'Days should be greater than 0')
    });
    
    const defaultValues = {
        distance: 0,
        month: null,
        vehicle: '',
        days: 0
    };
    
    const formMethods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: defaultValues
    });
    
    const {
        setValue,
        reset: reset,
        handleSubmit: handleSubmit,
        formState: { isSubmitting: isSubmitting },
        watch
    } = formMethods;
    
    const values = watch();

    useEffect(() => {
        console.log(defaultData);

        setValue('distance', defaultData?.distance);
        setValue('month', defaultData?.month);
        setValue('vehicle', defaultData?.vehicle);
        setValue('days', defaultData?.days);

        setAllowBatta(true);
    }, [defaultData]);

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const onSubmit = handleSubmit(async(data) => {
        console.log(data);

        try{
            moveToFormView(data.month, data.vehicle, data.days, allowBatta, data.distance);
        }
        catch(error){
            setSnackBar({
                message: {error},
                color: "red",
                open: true
            });
        }
    });

    const getStepperFillerCount = useCallback(() => {
        if(values){
            let count = 0;

            if(values.distance > 0){
                count++;
            }

            if(values.month){
                count++;
            }

            if(values.vehicle){
                count++;
            }

            if(values.days > 0){
                count++;
            }

            return count;
        }
        
        return 0;
    }, [values]);

    const handleCloseMonthPicker = (event) => {
        console.log(event);

        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpenMonthPicker(false);
    };

    return (
        <>
            <Stack
                direction='column'
                justifyContent='center'
                alignItems='start'
                px={10}
                ml={10}
                data-aos="fade-right"
            >
                <Button 
                    variant="text" 
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {navigate('/map');}}
                    sx={{
                        textTransform: 'none',
                        mb: 2,
                        color: 'white',
                        '&: hover': {
                            transform: 'scale(1.1)'
                        }
                    }}
                >
                    Edit Map
                </Button>

                {/* Title */}
                <Typography
                    fontWeight={800}
                    fontSize={50}
                    letterSpacing={0.3}
                    lineHeight={1.25}
                    textAlign='left'
                    color="white"
                    pb={2.5}
                >
                    Plan Your Perfect Tour<br />Instantly!
                </Typography>

                {/* Description */}
                <Typography
                    fontWeight={400}
                    fontSize={22}
                    letterSpacing={0.5}
                    lineHeight={1.25}
                    textAlign='left'
                    color='rgba(251, 251, 251, 1)'
                >
                    Easily estimate your tour costs with real-time distance, vehicle, and day selections.
                </Typography>

                <Card
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        borderRadius: '8px',
                        width: '100%',
                        py: 5,
                        mt: 10
                    }}
                >
                    <Stack 
                        px={10}
                    >
                        {/* Stepper */}
                        <Stack
                            direction='column'
                            justifyContent='center'
                            alignItems='center'
                            pt={2}
                        >
                            <CalculatorStepper 
                                step={getStepperFillerCount()}
                            />
                        </Stack>

                        {/* Form */}
                        <FormProviderComponent methods={formMethods} onSubmit={onSubmit}>
                            <Grid
                                direction='row'
                                justifyContent='space-between'
                                alignItems='start'
                                width='100%'
                                pt={8}
                                container
                                spacing={5}
                            >
                                <Grid
                                    size={3}
                                >
                                    {Label('Distance (Km) *')}

                                    <TextFieldComponent 
                                        name='distance'
                                        label=''
                                        fullWidth
                                        contrastTheme
                                        disabled={!allowDistanceEdit}
                                    />
                                </Grid>

                                <Grid
                                    size={3}
                                >
                                    {Label('Month *')}

                                    <Button
                                        ref={anchorRef}
                                        fullWidth
                                        disableRipple
                                        sx={{
                                            p: 0,
                                            '&: hover': {
                                                backgroundColor: 'transparent'
                                            },
                                            textTransform: 'none'
                                        }}
                                        onClick={() => setOpenMonthPicker(status => !status)}
                                    >
                                        <Stack
                                            direction='row'
                                            justifyContent='space-between'
                                            alignItems='center'
                                            sx={{
                                                width: '100%',
                                                backgroundColor: 'white',
                                                borderRadius: '8px'
                                            }}
                                            py={2}
                                            my={1.5}
                                        >
                                            <Typography
                                                fontWeight={600}
                                                fontSize={16}
                                                color='rgba(175, 175, 175, 1)'
                                                pl={2}
                                                textAlign='left'
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden !important'
                                                }}
                                            >
                                                {values.month}
                                            </Typography>

                                            <InsertInvitationIcon 
                                                sx={{
                                                    width: '20px',
                                                    height: '20px',
                                                    color: '#5E5959',
                                                    pr: 2
                                                }}
                                            />
                                        </Stack>
                                    </Button>
                                </Grid>

                                <Grid
                                    size={3}
                                >
                                    {Label('# Days *')}

                                    <TextFieldComponent 
                                        name='days'
                                        label=''
                                        fullWidth
                                        contrastTheme
                                    />
                                </Grid>

                                <Grid
                                    size={3}
                                >
                                    {Label('Vehicle *')}

                                    <SelectionComponent 
                                        name="vehicle"
                                        label=""
                                        list={VEHICLE}
                                        fullWidth={false}
                                        contrastTheme
                                        showListIcons
                                    />
                                </Grid>
                            </Grid>

                            {/* Next */}
                            <Stack
                                direction='row'
                                justifyContent='end'
                                alignItems='center'
                                width='100%'
                                py={3}
                            >
                                <ButtonComponent 
                                    label='Next'
                                    type="submit"
                                    inLoading={buttonLoading}
                                    sx={{
                                        width: '150px'
                                    }}
                                />
                            </Stack>
                        </FormProviderComponent>
                    </Stack>
                </Card>
            </Stack>

            <Popper
                open={openMonthPicker}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="top-start"
                transition
                disablePortal
                sx={{
                    width: anchorRef.current?.offsetWidth || undefined,
                    minWidth: 300
                }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                        }}
                    >
                        <Paper sx={{mt: -1}}>
                            <ClickAwayListener onClickAway={handleCloseMonthPicker}>
                                <Stack>
                                    <CustomMonthCalender 
                                        selectedValue={values.month}
                                        onSelectMonth={(month) => {
                                            setValue('month', month);
                                            setOpenMonthPicker(false);
                                        }}
                                    />
                                </Stack>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <SnackbarComponent 
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />
        </>
    );
}

export default CalculatorWebFilter;